import { useState } from "react";

import type { Process } from "@/api/types";
import {
  ProcessIconGlyph,
  resolveProcessIcon,
} from "@/components/ui/processIconLibrary";

/**
 * Render a process icon — prefers the platform-managed ``icon_url``
 * (PR1 of the cart redesign, served from on-disk seed via PR8), then
 * the built-in library icon the admin picked (``icon_key`` →
 * ``processIconLibrary``). Falls back to a 2-letter glyph (e.g. "3D",
 * "LA") when:
 *
 *   - no asset and no library pick is set on the process,
 *   - the asset URL 404s at runtime (server bytes vanished — historical
 *     dev/staging regression, defended via ``onError`` below) and no
 *     library pick exists,
 *   - the stored ``icon_key`` no longer resolves in the library.
 *
 * The ``slug`` prop is always passed so the glyph can be derived even
 * when ``displayName`` is null. The 2-letter glyph keeps the section
 * recognizable (e.g. "3D" for ``print_3d``, "LA" for ``laser``)
 * instead of falling back to a single "?".
 *
 * API note: takes raw ``iconUrl`` + ``slug`` + optional ``displayName``
 * so the same component renders in cart (using ``Process``) and in
 * platform admin (using ``PlatformProcessRow``) without forcing the
 * caller to construct a Process-shaped object.
 */
function deriveGlyph(slug: string, displayName?: string | null): string {
  const source = displayName?.trim() || slug;
  if (!source) return "?";
  // First word's first 2 chars uppercased — "3D" for "3D tisk", "LA"
  // for "laser", "PR" for "print_3d" when no display_name.
  const firstWord = source.split(/\s|_/u)[0] ?? source;
  return firstWord.slice(0, 2).toUpperCase();
}

export interface OperationIconProps {
  iconUrl?: string | null;
  iconKey?: string | null;
  /** ``operation_category_color`` — inks the library icon's "category"
   * variant with the process's system category colour. */
  categoryColor?: string | null;
  slug: string;
  displayName?: string | null;
  size?: number;
  className?: string;
}

export function OperationIcon({
  iconUrl,
  iconKey,
  categoryColor,
  slug,
  displayName,
  size = 24,
  className = "",
}: OperationIconProps): JSX.Element {
  const [broken, setBroken] = useState(false);

  if (iconUrl && !broken) {
    return (
      <img
        src={iconUrl}
        alt=""
        className={className}
        style={{ width: size, height: size, objectFit: "contain" }}
        onError={() => setBroken(true)}
        data-testid={`op-icon-${slug}`}
        aria-hidden="true"
      />
    );
  }
  if (iconKey && resolveProcessIcon(iconKey)) {
    return (
      <span data-testid={`op-icon-key-${slug}`} className="inline-flex">
        <ProcessIconGlyph
          token={iconKey}
          size={size}
          categoryColor={categoryColor}
          className={className}
        />
      </span>
    );
  }
  const glyph = deriveGlyph(slug, displayName);
  return (
    <span
      className={`inline-flex items-center justify-center rounded font-semibold ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(10, size / 2.4) }}
      data-testid={`op-icon-fallback-${slug}`}
      aria-hidden="true"
    >
      {glyph}
    </span>
  );
}

/**
 * Convenience adapter for cart callers that already hold a ``Process``
 * row. Mirrors the legacy API so PartCard/OperationSection/CartSummaryV2
 * don't have to be updated when the icon component refactored to flat
 * props.
 */
export function OperationIconForProcess({
  process,
  slug,
  size,
  className,
}: {
  process: Pick<
    Process,
    "icon_url" | "icon_key" | "operation_category_color" | "display_name"
  > | null;
  slug: string;
  size?: number;
  className?: string;
}): JSX.Element {
  return (
    <OperationIcon
      iconUrl={process?.icon_url}
      iconKey={process?.icon_key}
      categoryColor={process?.operation_category_color}
      slug={slug}
      displayName={process?.display_name}
      size={size}
      className={className}
    />
  );
}
